import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Truck, Package, Plane, Ship, Warehouse, Clock, Shield, Globe, CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

const ServicesPage = () => {
  const services = [
    {
      icon: Truck,
      title: "Load Transport",
      subtitle: "Full & Partial Loads",
      description: "Comprehensive trucking solutions for full truckload (FTL) and less-than-truckload (LTL) shipments. Our extensive fleet and route optimization ensure cost-effective and timely deliveries.",
      features: [
        "Full Truckload (FTL) Services",
        "Less-Than-Truckload (LTL) Options",
        "Temperature-Controlled Transport",
        "Oversized Load Handling"
      ],
      image: "https://images.unsplash.com/photo-1632517306067-b54ab4d1f98d?w=800&q=80"
    },
    {
      icon: Package,
      title: "Parcel Delivery",
      subtitle: "Domestic & International",
      description: "Fast and reliable parcel delivery services for businesses and individuals. From small packages to bulk shipments, we handle every delivery with care.",
      features: [
        "Same-Day Delivery Options",
        "International Parcel Services",
        "Package Insurance Coverage",
        "Real-Time Tracking"
      ],
      image: "https://images.unsplash.com/photo-1769477145932-edd5a9d7567c?w=800&q=80"
    },
    {
      icon: Plane,
      title: "Express Shipping",
      subtitle: "Time-Critical Deliveries",
      description: "When time is of the essence, our express shipping services deliver. Priority handling and dedicated routes ensure your urgent shipments arrive on schedule.",
      features: [
        "24-48 Hour Delivery",
        "Priority Handling",
        "Dedicated Air Freight",
        "Door-to-Door Service"
      ],
      image: "https://images.unsplash.com/photo-1766793110924-98e05b48eadc?w=800&q=80"
    }
  ];

  const additionalServices = [
    {
      icon: Ship,
      title: "Sea Freight",
      description: "Cost-effective ocean shipping for large cargo volumes"
    },
    {
      icon: Warehouse,
      title: "Warehousing",
      description: "Secure storage and distribution center solutions"
    },
    {
      icon: Globe,
      title: "Customs Brokerage",
      description: "Expert handling of international customs clearance"
    }
  ];

  const benefits = [
    { icon: Clock, text: "Fast Turnaround Times" },
    { icon: Shield, text: "Fully Insured Shipments" },
    { icon: Globe, text: "Global Coverage" }
  ];

  return (
    <div className="min-h-screen pt-16" data-testid="services-page">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1632517306067-b54ab4d1f98d?w=1920&q=80"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-sm font-medium text-blue-400 uppercase tracking-wider">Our Services</span>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mt-4 mb-6 tracking-tight uppercase"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Logistics Solutions<br />For Every Need
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              From express parcels to heavy freight, we offer comprehensive shipping solutions tailored to your business requirements.
            </p>

            <div className="flex flex-wrap gap-6 mt-8">
              {benefits.map((benefit) => (
                <div key={benefit.text} className="flex items-center gap-2 text-white">
                  <benefit.icon className="w-5 h-5 text-blue-400" />
                  <span className="text-sm">{benefit.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="space-y-24">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                data-testid={`service-section-${index}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="w-14 h-14 bg-blue-50 rounded-sm flex items-center justify-center mb-6">
                    <service.icon className="w-7 h-7 text-blue-600" />
                  </div>
                  <span className="text-sm font-medium text-blue-600 uppercase tracking-wider">
                    {service.subtitle}
                  </span>
                  <h2 
                    className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-4 tracking-tight"
                    style={{ fontFamily: 'var(--font-heading)' }}
                  >
                    {service.title}
                  </h2>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link to="/contact">
                    <Button className="rounded-sm bg-slate-900 hover:bg-slate-800">
                      Get a Quote <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="w-full rounded-sm shadow-lg"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-blue-600 uppercase tracking-wider">More Solutions</span>
            <h2 
              className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 tracking-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Additional Services
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div
                key={service.title}
                className="bg-white p-8 rounded-sm border border-slate-200 shadow-sm hover:-translate-y-1 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-12 h-12 bg-slate-900 rounded-sm flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  {service.title}
                </h3>
                <p className="text-slate-600">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 
            className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Need a Custom Solution?
          </h2>
          <p className="text-lg text-slate-400 mb-8">
            Our logistics experts are ready to design a tailored solution for your unique requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="rounded-sm bg-blue-600 hover:bg-blue-700 px-8 h-12">
                Contact Sales
              </Button>
            </Link>
            <Link to="/tracking">
              <Button variant="outline" className="rounded-sm border-slate-600 text-white hover:bg-slate-800 px-8 h-12">
                Track Shipment
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
